import React, { useEffect, useMemo, useState } from 'react';

import { faEquals, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import { sortTablesByUpperCaseFirst } from 'constants/global';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { JoinRow } from 'interfaces/report-templates.interface';
import { Button, Input, SelectFormInput } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  control?: Control<Record<string, unknown>>;
  selectedTable: string | null;
  onCloseModal: VoidFunction;
  onSubmit: (joins: JoinRow[]) => void;
  initialJoins?: JoinRow[];
  selectedJoins?: JoinRow[];
};

type JoinShape = {
  joinColumn?: { tableName?: string; name?: string; displayType?: string };
  column?: { tableName?: string; name?: string; displayType?: string };
};
const isJoinShape = (x: unknown): x is JoinShape => {
  return typeof x === 'object' && x !== null && ('joinColumn' in x || 'column' in x);
};

const CreateReportTemplateJoinModal: React.FC<Props> = ({
  selectedColumnsData = [],
  control,
  selectedTable,
  onCloseModal,
  onSubmit,
  initialJoins,
  selectedJoins,
}) => {
  const { t } = useTranslation();

  const idCounterRef = React.useRef<number>(1000000000);

  const createNewJoin = (index: number): JoinRow => {
    const id = idCounterRef.current;
    idCounterRef.current = idCounterRef.current + 1;
    return {
      id,
      leftTable: index === 0 ? selectedTable ?? '' : 'Previous Results',
      rightTable: '',
      leftColumn: '',
      rightColumn: '',
    };
  };

  const makeStableId = (idx: number): number => {
    return idx + 1;
  };

  const [joins, setJoins] = useState<JoinRow[]>([]);
  const { setValue, getValues } = useFormContext();
  const [availableColumns, setAvailableColumns] = useState<ReportingColumn[]>(selectedColumnsData ?? []);

  const mergeColumnsIntoAvailable = (colsToAdd: ReportingColumn[] | undefined) => {
    if (!colsToAdd || colsToAdd.length === 0) {
      return;
    }
    setAvailableColumns((prev) => {
      const existingKeys = new Set(prev.map((c) => `${c.tableName}::${c.name}`));
      const merged = [...prev];
      for (const c of colsToAdd) {
        const key = `${c.tableName}::${c.name}`;
        if (!existingKeys.has(key)) {
          merged.push(c);
          existingKeys.add(key);
        }
      }
      return merged;
    });
  };

  useEffect(() => {
    setAvailableColumns(selectedColumnsData ?? []);
  }, [selectedColumnsData]);

  const handleJoinChange = (index: number, field: keyof JoinRow, value: string) => {
    setJoins((prev) => prev.map((join, i) => (i === index ? { ...join, [field]: value } : join)));
    if (field === 'rightTable' && value) {
      const colsToAdd = (selectedColumnsData || []).filter((c) => c.tableName === value);
      mergeColumnsIntoAvailable(colsToAdd);
    }
  };

  const addJoin = () => {
    setJoins((prev) => [...prev, createNewJoin(prev.length)]);
  };

  const removeJoin = (index: number) => {
    setValue(`rightTable-${index}`, null);
    setValue(`leftColumn-${index}`, null);
    setValue(`rightColumn-${index}`, null);
    setJoins((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      const normalized =
        updated.length === 0
          ? [createNewJoin(0)]
          : updated.map((j, i) => (i === 0 ? { ...j, leftTable: selectedTable ?? '' } : j));

      normalized.forEach((j, i) => {
        setValue(`rightTable-${i}`, j.rightTable ? { label: j.rightTable, value: j.rightTable } : null);
        setValue(`leftColumn-${i}`, j.leftColumn ? { label: j.leftColumn, value: j.leftColumn } : null);
        setValue(`rightColumn-${i}`, j.rightColumn ? { label: j.rightColumn, value: j.rightColumn } : null);
      });

      return normalized;
    });
  };

  const getMatchingColumns = (leftColumnName: string, leftTable: string, rightTable: string) => {
    if (!leftColumnName || !leftTable || !rightTable) {
      return [];
    }
    const isLeftPrevious = String(leftTable).trim() === 'Previous Results';
    const isRightPrevious = String(rightTable).trim() === 'Previous Results';

    const leftColumn = (availableColumns || []).find((col) =>
      isLeftPrevious ? col.name === leftColumnName : col.tableName === leftTable && col.name === leftColumnName
    );
    if (!leftColumn) {
      return [];
    }
    const rightTableColumns = isRightPrevious
      ? availableColumns || []
      : (availableColumns || []).filter((col) => col.tableName === rightTable);

    const compatible = rightTableColumns.filter((c) => {
      if (leftColumn.displayType && c.displayType) {
        return c.displayType === leftColumn.displayType;
      }
      return c.columnType === leftColumn.columnType;
    });

    const sameName = compatible.find((c) => c.name === leftColumnName);
    const others = compatible.filter((c) => c.name !== leftColumnName);
    const ordered = sameName ? [sameName, ...others] : others;
    return ordered.map((col) => ({ label: col.name, value: col.name }));
  };

  const isAddDisabled = useMemo(() => {
    return !joins.every(
      (join) => join.leftTable?.trim() && join.rightTable?.trim() && join.leftColumn?.trim() && join.rightColumn?.trim()
    );
  }, [joins]);

  const rightTableOptions = useMemo(() => {
    const names = Array.from(new Set((selectedColumnsData || []).map((column) => column.tableName))).map((name) => ({
      name,
    }));
    const sorted = sortTablesByUpperCaseFirst(names);
    return sorted.map((table) => ({ label: table.name, value: table.name }));
  }, [selectedColumnsData]);

  const getCommonColumnsByType = (leftTable: string) => {
    if (!leftTable) {
      return [];
    }
    const sourceCols =
      String(leftTable).trim() === 'Previous Results'
        ? availableColumns
        : availableColumns.filter((c) => c.tableName === leftTable);
    return sourceCols.map((col) => ({ label: col.name, value: col.name }));
  };

  const handleLeftColumnSelect = (index: number, table: string, columnName: string) => {
    const isPreviousResults = String(table).trim() === 'Previous Results';

    const selectedColumn = (availableColumns || []).find((col) =>
      isPreviousResults ? col.name === columnName : col.tableName === table && col.name === columnName
    );

    setJoins((prev) =>
      prev.map((j, i) =>
        i === index
          ? {
              ...j,
              leftColumn: columnName,
              leftDisplayType: selectedColumn?.displayType || '',
              rightColumn: '',
            }
          : j
      )
    );
  };

  const handleRightColumnSelect = (index: number, value: string) => {
    setJoins((prev) => prev.map((j, i) => (i === index ? { ...j, rightColumn: value } : j)));
  };

  const sourceJoins = selectedJoins ?? initialJoins;
  const sourceJoinsJson = JSON.stringify(sourceJoins);

  useEffect(() => {
    const parsedSourceRaw = sourceJoinsJson ? (JSON.parse(sourceJoinsJson) as unknown[]) : undefined;
    const parsedSource = parsedSourceRaw
      ? parsedSourceRaw.map((s, idx) => {
          if (isJoinShape(s)) {
            const leftTable = s.column?.tableName || '';
            const rightTable = s.joinColumn?.tableName || '';
            const leftColumn = s.column?.name || '';
            const rightColumn = s.joinColumn?.name || '';
            const id = makeStableId(idx);
            return {
              id,
              leftTable,
              rightTable,
              leftColumn,
              rightColumn,
              leftDisplayType: s.column?.displayType || '',
              rightDisplayType: s.joinColumn?.displayType || '',
            } as JoinRow;
          }
          return s as JoinRow;
        })
      : undefined;

    if (parsedSource?.length) {
      const referencedTables = new Set<string>();
      parsedSource.forEach((j) => {
        if (j.rightTable) {
          referencedTables.add(j.rightTable);
        }
        if (j.leftTable && String(j.leftTable).trim() !== 'Previous Results') {
          referencedTables.add(j.leftTable);
        }
      });

      if (referencedTables.size) {
        const colsToAdd = (selectedColumnsData || []).filter((c) => referencedTables.has(c.tableName));
        mergeColumnsIntoAvailable(colsToAdd);
      }

      setJoins(parsedSource);

      parsedSource.forEach((join, idx) => {
        setValue(`rightTable-${idx}`, {
          label: join.rightTable,
          value: join.rightTable,
        });

        setValue(`leftColumn-${idx}`, {
          label: join.leftColumn,
          value: join.leftColumn,
        });

        setValue(`rightColumn-${idx}`, {
          label: join.rightColumn,
          value: join.rightColumn,
        });
      });
    } else {
      setJoins([
        {
          id: Date.now(),
          leftTable: selectedTable ?? '',
          rightTable: '',
          leftColumn: '',
          rightColumn: '',
        },
      ]);
    }
  }, [sourceJoinsJson, selectedTable, selectedColumnsData?.length, selectedColumnsData, setValue, getValues]);

  return (
    <div className="pt-4 mb-2">
      {joins.map((join, idx) => (
        <div key={`join-${join.id}-${idx}`} className="flex gap-1 items-center mt-3">
          <Input type="text" value={join.leftTable} inputClassName="!w-[10rem] !text-bodyMd-semibold" disabled />
          <FontAwesomeIcon icon={faPlus} className="text-neutral-900 dark:text-neutral-100" />
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`rightTable-${idx}`}
            control={control as Control<Record<string, unknown>>}
            options={rightTableOptions}
            onSelectValue={(option) => handleJoinChange(idx, 'rightTable', option?.value ?? '')}
            placeholder={t('SelectTable')}
            isClearable
          />
          <span className="text-neutral-900 dark:text-neutral-100">On</span>
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`leftColumn-${idx}`}
            control={control as Control<Record<string, unknown>>}
            options={(() => {
              const options = getCommonColumnsByType(join.leftTable);
              return options;
            })()}
            onSelectValue={(option) => {
              handleLeftColumnSelect(idx, join.leftTable, option?.value || '');
            }}
            placeholder={t('SelectColumn')}
            disabled={!join.leftTable}
          />
          <FontAwesomeIcon icon={faEquals} className="text-neutral-900 dark:text-neutral-100" />
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`rightColumn-${idx}`}
            control={control as Control<Record<string, unknown>>}
            options={(() => {
              const options = getMatchingColumns(join.leftColumn, join.leftTable, join.rightTable);
              return options;
            })()}
            onSelectValue={(option) => {
              const value = option?.value || '';
              handleRightColumnSelect(idx, value);
            }}
            placeholder={t('SelectColumn')}
            disabled={!join.leftColumn}
          />

          <Button
            type="button"
            onClick={() => removeJoin(idx)}
            variant={ButtonVariants.DESTRUCTIVE}
            size={ButtonSizeVariants.SM}
            className="w-6"
          >
            <FontAwesomeIcon icon={faTrashCan} className="text-white-00 w-4 h-4" />
          </Button>
        </div>
      ))}

      <Button
        className="mt-3"
        type="button"
        onClick={addJoin}
        variant={ButtonVariants.OUTLINE}
        size={ButtonSizeVariants.SM}
        disabled={isAddDisabled}
      >
        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
        {t('AddJoin')}
      </Button>

      <ModalControlButtons
        isShownSubmit
        onClose={onCloseModal}
        saveButtonText={'Create'}
        disabled={isAddDisabled}
        onSubmit={() => onSubmit(joins)}
      />
    </div>
  );
};

export default CreateReportTemplateJoinModal;
